import { useRecipient } from '../components/settings/shared/settings-queries';
import { isBriefConversation, useConversations } from './conversations';
import { onboardingRouteDecision, type OnboardingRoute } from './onboarding-route';

export interface OnboardingEntry {
  // The resolved route AFTER the resume→kickoff fallback below: a `resume` with nothing to resume becomes
  // a fresh `kickoff`, so a consumer never has to handle "resume but no thread".
  route: OnboardingRoute;
  resumeConversationId: string | null;
  // Both the recipient AND the conversation list have settled, so the decision is trustworthy. Until then
  // it is `none`/`false` and consumers must HOLD (show a loader, don't commit to the bare home) rather than
  // act on an unresolved guess — the post-signup index lag makes "not loaded yet" common.
  resolved: boolean;
  // The inputs have stopped LOADING, whether or not they arrived. A failed recipient or conversations read
  // leaves `resolved` false for good, so an action that only needs to know "is this a brand-new account"
  // (New Chat) gates on this instead — otherwise one failed read disables it for the rest of the session.
  settled: boolean;
  // The account has at least one chat of its own (briefs excluded). Only meaningful once `resolved`.
  hasConversations: boolean;
}

// The onboarding conversation to resume is a first-party (web/app) thread; an externally-owned WhatsApp/SMS
// thread is never the onboarding chat. Undefined channel = a plain web-created conversation.
const RESUMABLE_CHANNELS = new Set(['WEB', 'APP']);

/**
 * The web onboarding-entry decision, DERIVED from server state (the recipient's onboarding status + whether
 * any conversation exists) rather than a `?welcome=true` URL param. This is the single place the client
 * classifies onboarding entry; it wraps the pure `onboardingRouteDecision` (the native port — see
 * `lib/onboarding-route.ts`) over the shared React-Query caches, so every consumer agrees and no navigation
 * path can strand a user by forgetting a flag.
 *
 * The in-session once-guard (`kickoffAlreadyFired`) lives at the ACTION site (the AppFrame trigger / the
 * ChatSurface autostart ref), not here — this hook reports the natural route so the presentation consumers
 * (overlay open, chat size) keep reading it while the greeting is in flight.
 */
export function useOnboardingEntry(): OnboardingEntry {
  const { data: recipient, isLoading: recipientLoading, isError: recipientError } = useRecipient();
  const { state } = useConversations(true);

  const conversationsReady = state.kind === 'ready';
  // Briefs are system-created and never the onboarding thread, so they count neither as a thread to resume
  // nor as evidence the user has chatted (the list carries them since it mirrors the phones' drawer). Nor does
  // the Business Manager chat: Wix's preview, held here before the owner ever opened Symphony, so a new owner
  // with only that chat still gets the kickoff instead of being resumed into it.
  const conversations = conversationsReady ? state.conversations.filter((c) => !isBriefConversation(c) && !c.wixBmChat) : [];
  const resolved = conversationsReady && !recipientLoading && !recipientError && !!recipient;
  const settled = state.kind !== 'loading' && !recipientLoading;
  const hasConversations = conversations.length > 0;

  if (!resolved) return { route: 'none', resumeConversationId: null, resolved: false, settled, hasConversations };

  // The list is already newest-first (conversations data layer), so the first first-party thread is the one
  // to resume.
  const resumeConversationId =
    conversations.find((c) => !c.communicationChannel || RESUMABLE_CHANNELS.has(c.communicationChannel))?.id ?? null;

  const route = onboardingRouteDecision({
    onboardingState: recipient.onboardingState,
    finishedOnboarding: recipient.finishedOnboarding,
    hadStoredConversation: conversations.length > 0,
    kickoffAlreadyFired: false,
  });

  // native `enterOnboardingResume` falls back to a fresh kickoff when there is no thread to reopen.
  if (route === 'resume' && !resumeConversationId) return { route: 'kickoff', resumeConversationId: null, resolved: true, settled, hasConversations };
  return { route, resumeConversationId: route === 'resume' ? resumeConversationId : null, resolved: true, settled, hasConversations };
}
